import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '../Button';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import GatingToolTip from './GatingToolTip';
import UnlockIcon from '../../svg/icons/Resy_Unlock_Icon.svg';
import './GatingMessage.scss';
import { STRIPE_FORM_CONTEXT } from '../../constants/paymentProcessors';
import StripeForm from '../StripeForm/StripeForm';
import CardAdded from '../CardAdded/CardAdded';
import Icon from '../Icon';
import { isWidget } from '../../constants/appContext';
import { sanitizeString } from '../../utils/sanitizeString';

const ADD_CARD = 'add_card';
const GATING_MESSAGE = 'gating_message';
const CARD_ADDED_SUCCESSFUL = 'card_added_successful';

const GatingMessage = (props) => {
  const {
    venueName,
    Analytics,
    closeModal,
    context,
    gatingErrors,
    ResyAmexConfig,
    isGda,
    exclusiveId,
  } = props;

  const [viewToBeDisplayed, setViewToBeDisplayed] = useState(GATING_MESSAGE);
  const platform = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent)
    ? 'Mobile Web'
    : 'Desktop';

  const {
    title,
    message_1: gatingMessage1,
    message_2: gatingMessage2,
    brand_icon: brandIcon,
    add_card_text: addCardText,
    learn_more_text: learnMoreText,
    learn_more_link: learnMoreLink,
  } = gatingErrors?.[exclusiveId] ?? {};

  const { baseRequestUrl } = ResyAmexConfig;
  const imageUrl = `${baseRequestUrl}GDA/images/gda_lock_icon.gif`;
  const [animated, setAnimated] = useState(false);

  const getEventParams = (action) => {
    if (action === 'error message') {
      return ({
        venue: venueName,
        title: 'Something went wrong',
        platform,
        action,
        eventAsset: `${venueName} + ${action}`,
      });
    }
    return ({
      venue: venueName,
      title,
      platform,
      action,
      eventAsset: `${venueName} + ${action}`,
    });
  };

  useEffect(() => {
    if (gatingErrors?.[exclusiveId] && Object.keys(gatingErrors[exclusiveId]).length > 0) {
      Analytics.event('interact with gating modal', getEventParams('impression'));
    } else {
      Analytics.event('interact with gating modal', getEventParams('error message'));
    }
  }, [gatingErrors]);

  const close = () => {
    if (isWidget(context)) {
      window.parent.postMessage({ event: 'close' }, '*');
    } else {
      closeModal();
    }
  };
  const renderCloseButton = () => (
    <Button
      variant="close"
      aria-label={getTranslation({
        translationKey: 'close_payment',
        fallback: 'Close payment',
      })}
      onClick={close}
    />
  );

  const renderBackButton = (onClick = () => {}) => (
    <Button
      variant="back"
      aria-label={getTranslation({
        translationKey: 'go_back',
        fallback: 'Go back',
      })}
      onClick={onClick}
    />
  );

  const addCard = () => {
    Analytics.event(
      'interact with gating modal',
      getEventParams('add card CTA')
    );
    setViewToBeDisplayed(ADD_CARD);
  };

  const learnMore = () => {
    Analytics.event(
      'interact with gating modal',
      getEventParams('learn more CTA')
    );
    if (learnMoreLink) {
      window.open(learnMoreLink, '_blank');
    }
  };

  const getLockIcon = () => {
    if (animated) {
      return (
        <Icon
          component={UnlockIcon}
          iconClass="GatingMessage__Lock-anim"
          role="presentation"
          ariaLabel={getTranslation({ translationKey: 'unlocking_animation', fallback: 'Unlocking animation' })}
          height="120px"
          width="120px"
        />
      );
    }
    return (
      <img
        className="GatingMessage__Lock-anim"
        src={imageUrl}
        onLoad={() => {
          setTimeout(() => {
            setAnimated(true);
          }, [2000]);
        }}
        alt={getTranslation({ translationKey: 'unlocking_animation', fallback: 'Unlocking animation' })}
        data-testid="gating_error_lock_anim"
      />
    );
  };

  const getAddCardModal = () => {
    const {
      matchMedia, AutomationMode, stripePublishableKey,
    } = props;

    return (
      <>
        {renderBackButton(() => setViewToBeDisplayed(GATING_MESSAGE))}
        {renderCloseButton()}
        <StripeForm
          Analytics={Analytics}
          AutomationMode={AutomationMode}
          matchMedia={matchMedia}
          closeForm={(paymentMethod) => {
            if (paymentMethod) {
              setViewToBeDisplayed(CARD_ADDED_SUCCESSFUL);
            }
          }}
          context={STRIPE_FORM_CONTEXT.profile}
          stripePublishableKey={stripePublishableKey}
        />
      </>
    );
  };

  const renderDefaultMessage = () => (
    <div className="GatingMessage__generic-message">
      <TranslationProvider translationKey="generic_gating_header">
        <div className="GatingMessage__generic-error-header" data-testid="generic_error_header">Something went wrong</div>
      </TranslationProvider>
      <div className="GatingMessage__generic-error-text" data-testid="generic_error_text">
        <TranslationProvider translationKey="generic_gating_text">
          <span>
            Please check your connection and try again. If you are still experiencing issues, please reach out to our customer support team at&nbsp;
          </span>
        </TranslationProvider>
        <a className="GatingMessage__mail" target="_blank" rel="noreferrer" href="mailto:help@resy.com">help@resy.com</a>
        <span>.</span>
      </div>
    </div>
  );

  const displayGatingErrorMessage = () => {
    if (gatingErrors?.[exclusiveId] && Object.keys(gatingErrors[exclusiveId]).length > 0) {
      return (
        <>
          <div>
            <img
              height="32px"
              width="94px"
              alt={getTranslation({
                translationKey: 'brand_icon',
                fallback: 'Resy and American Express logo',
              })}
              src={brandIcon}
              data-testid="gating_error_brand_icon"
            />
          </div>

          <div
            className="GatingMessage__Headline"
            data-testid="gating_error_title"
            dangerouslySetInnerHTML={{
              __html: sanitizeString(title),
            }}
          />
          {gatingMessage1 && (
          <div
            className="GatingMessage__Message"
            data-testid="gating_error_message1"
          >
            <span
              className="GatingMessage__InlineText"
              dangerouslySetInnerHTML={{
                __html: sanitizeString(gatingMessage1),
              }}
            />
            {isGda && (
            <div className="GatingMessage__TooltipContainer">
              <GatingToolTip
                Analytics={Analytics}
                eventData={getEventParams('gda tooltip')}
              />
            </div>
            )}
          </div>
          )}
          {gatingMessage2 && (
          <div
            className="GatingMessage__Message"
            data-testid="gating_error_message2"
            dangerouslySetInnerHTML={{
              __html: sanitizeString(gatingMessage2),
            }}
          />
          )}
          <button
            className="GatingMessage__CTA button-primary"
            type="button"
            onClick={addCard}
            data-testid="gating_error_add_card_btn"
            aria-label={
              addCardText
              || getTranslation({
                translationKey: 'add_eligible_card',
                fallback: 'Add Your Eligible Card',
              })
            }
          >
            {addCardText
                  || getTranslation({
                    translationKey: 'add_eligible_card',
                    fallback: 'Add Your Eligible Card',
                  })}
          </button>
          <button
            className="GatingMessage__CTA button-secondary"
            type="button"
            onClick={() => learnMore(learnMoreLink)}
            data-testid="gating_error_learn_more_btn"
            aria-label={learnMoreText}
          >
            {learnMoreText}
          </button>
        </>
      );
    }
    return renderDefaultMessage();
  };

  const renderView = () => {
    const eventData = getEventParams('card added');
    switch (viewToBeDisplayed) {
      case ADD_CARD: {
        return getAddCardModal();
      }
      case CARD_ADDED_SUCCESSFUL:
        return (
          <>
            {renderCloseButton()}
            <CardAdded Analytics={Analytics} eventData={eventData} />
          </>
        );
      case GATING_MESSAGE: {
        return (
          <>
            <div className="GatingMessage-view">
              <div className="ModalNav">
                <Button
                  aria-label={getTranslation({
                    translationKey: 'close',
                    fallback: 'Close',
                  })}
                  variant="close"
                  onClick={close}
                  data-testid="gating_error_close_btn"
                />
              </div>
              <div className="GatingMessage__Lock-container">
                {imageUrl && getLockIcon()}
              </div>
              {displayGatingErrorMessage()}
            </div>
          </>
        );
      }
      default:
        return null;
    }
  };

  return <div className="GatingMessage">{renderView()}</div>;
};

GatingMessage.displayName = 'GatingMessage';

GatingMessage.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
    page: PropTypes.func,
  }).isRequired,
  closeModal: PropTypes.func,
  venueName: PropTypes.string.isRequired,
  context: PropTypes.string,
  gatingErrors: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
      message_1: PropTypes.string,
      message_2: PropTypes.string,
      brand_icon: PropTypes.string,
      add_card_text: PropTypes.string,
      learn_more_text: PropTypes.string,
      learn_more_link: PropTypes.string,
    }).isRequired
  ),
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  AutomationMode: PropTypes.bool,
  stripePublishableKey: PropTypes.string,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string,
  }).isRequired,
  isGda: PropTypes.bool.isRequired,
  exclusiveId: PropTypes.number.isRequired,
};

GatingMessage.defaultProps = {
  context: undefined,
  AutomationMode: false,
  stripePublishableKey: undefined,
  closeModal: () => {},
  gatingErrors: {},
};

GatingMessage.displayName = 'GatingMessage';

const mapStateToProps = (state) => ({
  matchMedia: state.matchMedia,
  configStatus: state.resyConfig.configStatus,
  stripePublishableKey: state.resyConfig.config?.stripe_publishable_key,
  gatingErrors: state.booking?.reservation?.gatingErrors,
  isGda: state.booking?.reservation?.isGda,
  venueName: state.booking?.reservation?.venueName,
  exclusiveId: state.booking?.reservation?.exclusiveId,
});

export { GatingMessage as ComponentUnderTest };
export default connect(mapStateToProps)(GatingMessage);
